import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import Loading from "../../../components/Loading";
import { triggerRerender } from "../../../redux/rerenderSlice";

export default function DebitCardModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const { control, setError, clearErrors, formState: { errors }, reset } = useForm();
  const [saving, setSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [customerDebitCard, setCustomerDebitCard] = useState(null)
  const [nmiGatewaySettings, setNmiGatewaySettings] = useState(null)
  const [debitCardSettingsError, setDebitCardSettingsError] = useState(null)
  const request = useRequest()
  // One instance per concern: the settings and card-on-file lookups run concurrently, and a shared
  // useRequest() would let one call's endpoint overwrite the other's (see KNOWN_WARTS.md).
  const settingsRequest = useRequest()
  const debitCardRequest = useRequest()
  const settingsInFlightFor = useRef(null)
  const debitCardInFlightFor = useRef(null)
  const customerId = modal.options?.customerId
  const firstName = useRef(null);
  const lastName = useRef(null);
  const zipCode = useRef(null);
  const inFlightCustomerId = useRef(null);

  const resetState = () => {
    setSaving(false)
    setIsLoading(true)
    setDebitCardSettingsError(null)
    setCustomerDebitCard(null)
    setNmiGatewaySettings(null)
    reset();
  }

  useEffect(() => {
    if (modal.type === 'debit_card_modal' && modal.is_active) {
      // This modal stays mounted in the Footer; drop the previous customer's card on file and
      // gateway settings before loading this customer's.
      resetState()
      inFlightCustomerId.current = customerId
      firstName.current.focus()
      getDebitCardSettings()
      getCustomerDebitCardActive()
    }
  }, [modal]);

  // In-flight flags live in refs keyed by customer: a flag on the useRequest() object is lost on the
  // next render and, shared between both lookups, made the card-on-file lookup always bail out.
  const getDebitCardSettings = async () => {
    const forCustomerId = customerId
    if (settingsInFlightFor.current === forCustomerId) return;
    settingsInFlightFor.current = forCustomerId;
    try {
      settingsRequest.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${forCustomerId}/debit_card_settings`)
      const response = await settingsRequest.get()
      if (inFlightCustomerId.current !== forCustomerId) return
      if (response.data.data === null) {
        setDebitCardSettingsError(response.data.message)
        setIsLoading(false)
      } else {
        setNmiGatewaySettings(response.data.data)
      }
    } catch (error) {
      if (inFlightCustomerId.current !== forCustomerId) return
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    } finally {
      if (settingsInFlightFor.current === forCustomerId) settingsInFlightFor.current = null;
    }
  }

  const getCustomerDebitCardActive = async () => {
    const forCustomerId = customerId
    if (debitCardInFlightFor.current === forCustomerId) return;
    debitCardInFlightFor.current = forCustomerId;
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/customer_debit_card`;
      endpoint += `?filters={"and":[{"column":"customer_id","value":"${forCustomerId}","operator":"="},{"column":"is_active","value":"1","operator":"="}]}`
      debitCardRequest.setEndpoint(endpoint)
      const response = await debitCardRequest.get()
      if (inFlightCustomerId.current === forCustomerId) {
        setCustomerDebitCard(response?.data?.data?.length === 1 ? response.data.data[0] : null)
      }
    } catch (error) {
      if (inFlightCustomerId.current !== forCustomerId) return
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    } finally {
      if (debitCardInFlightFor.current === forCustomerId) debitCardInFlightFor.current = null;
    }
  }

  useEffect(() => {
    if (nmiGatewaySettings) {
      const script = document.createElement('script');
      script.id = 'credit_card_collect_script'
      script.src = 'https://secure.nmi.com/token/Collect.js'
      script.dataset.tokenizationKey = nmiGatewaySettings.nmi_public_key
      script.onload = () => {
        window.CollectJS.configure({
          paymentSelector: "#saveCard",
          variant: "inline",
          price: 0.01,
          currency: "USD",
          country: "US",
          customCss: {
            "border-color": "rgb(219, 219, 219)",
            "border-style": "solid",
            "border-width": "1px",
            padding: "0px 10px",
            "line-height": 0,
            "border-radius": "6px",
            height: "30px"
          },
          fields: {
            ccnumber: {
              selector: "#ccnumber",
              title: "Card Number",
              placeholder: "0000 0000 0000 0000"
            },
            ccexp: {
              selector: "#ccexp",
              title: "Card Expiration",
              placeholder: "00 / 00"
            },
            cvv: {
              selector: "#cvv",
              title: "CVV Code",
              placeholder: "***"
            },
          },
          invalidCss: {
            "border-color": "#f84551",
          },
          validationCallback: function (field, status, message) {
            if (status) {
              clearErrors(field);
            } else {
              setError(field, { type: "error", message: message });
              setSaving(false)
            }
          },
          fieldsAvailableCallback: () => {
            setIsLoading(false)
          },
          callback: (response) => {
            submit(response.token, response.card);
          }
        });
      };
      document.head.appendChild(script);
      // Cleanup function to remove script on unmount
      return () => document.head.removeChild(script);
    }
  }, [nmiGatewaySettings]);

  const submit = async (token, card) => {
    try {
      const payload = {
        card_holder_first_name: firstName.current.value,
        card_holder_last_name: lastName.current.value,
        payment_token: token,
        zip_code: zipCode.current.value,
        expiration: card.exp,
        card: card.number?.slice(-4),
        type: card.type,
        processor_company_dropdown_option_id: nmiGatewaySettings.nmi_gateway_processor_company_dropdown_option_id
      }
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/debit_card`)
      const response = await request.post(payload)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
        dispatch(triggerRerender({ subpanelId: 'customer_debit_card' }))
        handleCancel()
      } else {
        setSaving(false)
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      dispatch(setNotification({ type: 'error', body: `Error: ${message}` }))
      setSaving(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateFields() && Object.keys(errors).length === 0) {
      setSaving(true);
    }
  }

  const validateFields = () => {
    let isValidated = true;
    if (!firstName.current.value) {
      setError('first_name', { type: "error", message: '' })
      isValidated = false;
    } else {
      clearErrors('first_name')
    }

    if (!lastName.current.value) {
      setError('last_name', { type: "error", message: '' })
      isValidated = false;
    } else {
      clearErrors('last_name')
    }

    if (!zipCode.current.value) {
      setError('zipcode', { type: "error", message: '' })
      isValidated = false;
    } else {
      clearErrors('zipcode')
    }
    return isValidated;
  }

  const handleCancel = () => {
    inFlightCustomerId.current = null
    resetState()
    dispatch(setModal({
      is_active: false
    }));
  };

  return modal.is_active && modal.type === 'debit_card_modal' ? (
    <form id="debit_card_modal_form" className="lox-form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-3">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Add Debit Card
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>

          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "100px", overflow: 'hidden' }}
          >
            {(isLoading === true) && <Loading />}
            <div className={`columns mb-0 is-flex-direction-column mt-3`} style={{ opacity: isLoading === true ? '0' : 1 }}>
              <div className="column">
                {debitCardSettingsError ? (
                  <h3 className="has-text-danger has-text-weight-bold">{debitCardSettingsError}</h3>
                ) : (
                  <>
                    <h3>{`A customer can only have one active card on their account and by adding one it will become the only card for this customer.`}</h3>
                    {customerDebitCard && (
                      <h3 className="has-text-danger has-text-weight-bold">{`* This customer already has a card on file ending in ${customerDebitCard.last_four}`}</h3>
                    )}
                  </>
                )}
              </div>
              <div className="column">
                <div className="field">
                  <label htmlFor="first_name">
                    First Name
                    <span className="has-text-danger">*</span>
                  </label>
                  <Controller
                    name="first_name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <input {...field} type="text" id="first_name" className="input is-small" onChange={validateFields} ref={firstName} />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.first_name && <span>{errors.first_name.message ? errors.first_name.message : "First Name is required"}</span>}
                  </p>
                </div>
              </div>
              <div className="column">
                <div className="field">
                  <label htmlFor="last_name">
                    Last Name
                    <span className="has-text-danger">*</span>
                  </label>
                  <Controller
                    name="last_name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <input {...field} type="text" id="last_name" onChange={validateFields} className="input is-small" ref={lastName} />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.last_name && <span>{errors.last_name.message ? errors.last_name.message : "Last Name is required"}</span>}
                  </p>
                </div>
              </div>
              <div className="column">
                <div className="field">
                  <label htmlFor="ccexp">
                    Expiration Date
                    <span className="has-text-danger">*</span>
                  </label>
                  <div id="ccexp"></div>
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.ccexp?.message && <span>{errors.ccexp.message ? errors.ccexp.message : "Expiration Date is required"}</span>}
                  </p>
                </div>
              </div>
              <div className="column">
                <div className="field">
                  <label htmlFor="cvv">
                    Security Code
                    <span className="has-text-danger">*</span>
                  </label>
                  <div id="cvv"></div>
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.cvv?.message && <span>{errors.cvv.message ? errors.cvv.message : "Security Code is required"}</span>}
                  </p>
                </div>
              </div>
              <div className="column">
                <div className="field">
                  <label htmlFor="ccnumber">
                    Card Number
                    <span className="has-text-danger">*</span>
                  </label>
                  <div id="ccnumber"></div>
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.ccnumber?.message && <span>{errors.ccnumber.message ? errors.ccnumber.message : "Last Name is required"}</span>}
                  </p>
                </div>
              </div>
              <div className="column">
                <div className="field">
                  <label htmlFor="zipcode">
                    Zipcode
                    <span className="has-text-danger">*</span>
                  </label>
                  <Controller
                    name="zipcode"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <input {...field} type="text" id="zipcode" onChange={validateFields} className="input is-small" ref={zipCode} />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.zipcode && <span>{errors.zipcode.message ? errors.zipcode.message : "Zipcode is required"}</span>}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
              <button onClick={handleSubmit} type="submit" id="saveCard" className="button is-link is-small" disabled={(saving || !!debitCardSettingsError) && 'disabled'}>
                {saving ? (
                  <>
                    Submitting
                    <Loading size="is-size-6" />
                  </>
                ) : 'Submit'}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
